import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { AuditTrailRow } from '@/types/audit'
import type {
  CatalogImportResult,
  CatalogStatusImportResult,
  CreatedProduct,
  ProductRecord,
  ProductCatalogEntry,
  ProductCatalogFacets,
  ProductCatalogRow,
  ProductCatalogFilters,
  ProductCatalogPage,
  SplitPreview,
  PoolPreview,
  StorageTempMappingChange,
  StorageTempMappingList,
} from '@/types/products'

/** `storageCondition` is resolved per tenant by the server, never sent. */
export type CreateCatalogEntryPayload = Omit<
  ProductCatalogEntry,
  'id' | 'createdAt' | 'updatedAt' | 'storageCondition'
>

/** One row of the status upload — the only three columns it sends back (z8nrz7e72t). */
export type CatalogStatusUploadRow = {
  productDescriptionCode: string
  active?: boolean
  note?: string
}

/** The tenant's ISBT 128 DIN settings (z8nrz7fxwh): what every minted DIN is made of. */
export type DinSettings = {
  /** False until someone saves them; products cannot be created until then. */
  configured: boolean
  facilityId: string
  flag: string
  startingNumber: number
  nextSequence: number
  /** The DIN the next product would get, and its check character. Null when not configured. */
  nextDin: string | null
  nextCheckCharacter: string | null
  updatedBy: string | null
  updatedAt: string | null
}

export type DinSettingsInput = Pick<DinSettings, 'facilityId' | 'flag' | 'startingNumber'>

export type CreateProductPayload = {
  catalogEntryId: string
  /**
   * Only for a bag that arrived with its own DIN (Receive, scanned or typed
   * in). Left out, the server mints the next DIN from the tenant's DIN
   * settings (z8nrz7fxwh).
   */
  din?: string
  /** The flag read with `din` from its barcode. */
  dinFlag?: string
  /** The check character typed with a hand-entered `din`; the server refuses a mismatch. */
  dinCheckCharacter?: string
  submissionId: string
  formDefinitionId: string
  procedureId?: string | null
  patientMrn?: string | null
  patientName?: string | null
  patientDob?: string | null
  /** Volume in the bag at creation — the figure split, pool and volume-update all work from. */
  volumeMl?: number
}

/** No DIN: every aliquot carries its parent's DIN and flag, set by the server (z8nrz7fxwh). */
export type SplitProductChildPayload = {
  /** Full product code including the division suffix, e.g. "S1155A0" — computed client side, see productSplit.ts. */
  productDescriptionCode: string
  volumeMl?: number
  limsValues?: Record<string, number>
}

export type PoolProductsPayload = {
  /** The products being combined. At least two; the server re-checks the safety rule. */
  sourceProductIds: string[]
  /** No DIN: the server mints the pooled product's own (z8nrz7fxwh). */
  catalogEntryId: string
  submissionId: string
  formDefinitionId: string
  procedureId?: string | null
  /** Why these products are being combined — recorded on the pooled product. */
  reason?: string
  /** Buffer added to the pool, in mL. Dilutes every pooled concentration. */
  bufferMl?: number
  // NOTE: no `volumeMl` and no `limsValues`. Both are derived on the server
  // from the member products (z8nrz7e32b) — the pooled volume is Σ member
  // volumes + buffer, and the dose is recalculated from the members' own
  // stored figures. Sending either from here is what this ticket removed.
}

/** The selection to price up, for `previewPool`. Creates nothing. */
export type PreviewPoolPayload = {
  sourceProductIds: string[]
  bufferMl?: number
  submissionId?: string
  /** Must match the pool's own — it decides which forms the recipient weight is read from. */
  procedureId?: string | null
}

/**
 * The proposed aliquots to price up, for `previewSplit`. Volumes only — the
 * preview creates nothing, so it needs no DINs.
 */
export type PreviewSplitPayload = {
  submissionId: string
  /** Must match the split's own, since it decides which forms the recipient weight is read from. */
  procedureId?: string | null
  children: { productDescriptionCode?: string; volumeMl?: number }[]
  /**
   * Lab values the operator typed for inputs no form answers, by Field Library
   * system name. Only fills gaps: a value on a form wins. Audited as entered by hand.
   */
  manualInputs?: Record<string, number>
}

export type SplitProductPayload = {
  submissionId: string
  formDefinitionId: string
  procedureId?: string | null
  /** Why the split is being performed — recorded on every aliquot's audit row. */
  reason?: string
  children: SplitProductChildPayload[]
  /**
   * Lab values the operator typed for inputs no form answers, by Field Library
   * system name. Only fills gaps: a value on a form wins. Audited as entered by hand.
   */
  manualInputs?: Record<string, number>
}

/** Shared fetch wrapper — bearer token + `X-Tenant-Id` header (inventoryApi pattern). */
async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId,
      ...init?.headers,
    }),
  })
  handleUnauthorized(res.status)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string | string[] } | null
    const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
    throw new Error(message || `Request failed: ${res.status}`)
  }
  return res.json() as Promise<T>
}

/**
 * Catalog filters → query string. Booleans and numbers are stringified, and a
 * falsy value is dropped rather than sent as '' — an empty filter must mean
 * "no filter", not "match the empty string", which every blank column would.
 */
function catalogQuery(filters: ProductCatalogFilters): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '' || value === false) continue
    qs.set(key, String(value))
  }
  const query = qs.toString()
  return query ? `?${query}` : ''
}

export const productsApi = {
  // ─── Product catalog (Configure → Products) ────────────────────────────────

  /**
   * One PAGE of the ISBT 128 catalog — 14,911 rows, so never the whole table.
   *
   * `tenantId` only satisfies the tenant guard in front of the permission
   * check; the catalog itself is global and the same for every tenant.
   */
  listCatalog: (
    tenantId: string,
    filters: ProductCatalogFilters = {},
  ): Promise<ProductCatalogPage> =>
    request<ProductCatalogPage>(`/products/catalog${catalogQuery(filters)}`, tenantId),

  /**
   * Resolve catalog entries by id — for callers that hold a PRODUCT and need the
   * code it was made from (the split dialog's base code, the pool dialog's
   * class check). They cannot scan the catalog for it any more: it is paged.
   */
  catalogEntriesByIds: (tenantId: string, ids: string[]): Promise<ProductCatalogRow[]> => {
    if (ids.length === 0) return Promise.resolve([])
    return request<ProductCatalogRow[]>(
      `/products/catalog/by-ids?ids=${encodeURIComponent(ids.join(','))}`,
      tenantId,
    )
  },

  /** Distinct values per filterable column, for the picker's dropdowns. */
  catalogFacets: (
    tenantId: string,
    filters: ProductCatalogFilters = {},
  ): Promise<ProductCatalogFacets> =>
    request<ProductCatalogFacets>(`/products/catalog/facets${catalogQuery(filters)}`, tenantId),

  createCatalogEntry: (
    tenantId: string,
    payload: CreateCatalogEntryPayload,
  ): Promise<ProductCatalogEntry> => {
    return request<ProductCatalogEntry>(`/products/catalog`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  /**
   * Bulk import (Configure → Products → Import).
   *
   * Idempotent on the product code, so re-importing the same file refreshes
   * those rows rather than appending a second copy of all 14,911 of them.
   */
  importCatalogEntries: (
    tenantId: string,
    entries: CreateCatalogEntryPayload[],
  ): Promise<CatalogImportResult> => {
    return request<CatalogImportResult>(`/products/catalog/import`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ entries }),
    })
  },

  // ─── Product code Active / Inactive (z8nrz7e72t) ────────────────────────────
  //
  // These write only the SELECTED TENANT's status and note on a code. The
  // catalog row itself is global and none of them can reach it.

  /** Activate / Inactivate the given codes, and/or set their note. */
  updateCatalogStatus: (
    tenantId: string,
    payload: { catalogEntryIds: string[]; active?: boolean; note?: string },
  ): Promise<{ updated: number }> =>
    request<{ updated: number }>(`/products/catalog/status`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  /** Activate ALL / Inactivate ALL — every code the filters match (all of them, unfiltered). */
  setAllCatalogStatus: (
    tenantId: string,
    filters: ProductCatalogFilters,
    active: boolean,
  ): Promise<{ updated: number }> =>
    request<{ updated: number }>(`/products/catalog/status/all${catalogQuery(filters)}`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ active }),
    }),

  /** The Excel upload: code + Active + Note per row, and nothing else. */
  importCatalogStatus: (
    tenantId: string,
    rows: CatalogStatusUploadRow[],
  ): Promise<CatalogStatusImportResult> =>
    request<CatalogStatusImportResult>(`/products/catalog/status/import`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),

  // ─── Storage temperature → storage condition mapping ───────────────────────
  //
  // Per tenant. A catalog code's mapped condition comes back on the entry as
  // `storageCondition`, and product put-away is checked against it.

  /** Every distinct catalog storage temperature, with its mapping and the unmapped count. */
  listStorageTempMappings: (tenantId: string): Promise<StorageTempMappingList> =>
    request<StorageTempMappingList>(`/products/storage-temp-mappings`, tenantId),

  /** Bulk set; `condition: null` clears a mapping. */
  setStorageTempMappings: (
    tenantId: string,
    mappings: StorageTempMappingChange[],
  ): Promise<{ updated: number }> =>
    request<{ updated: number }>(`/products/storage-temp-mappings`, tenantId, {
      method: 'PUT',
      body: JSON.stringify({ mappings }),
    }),

  // ─── ISBT 128 DIN settings (z8nrz7fxwh) ────────────────────────────────────
  //
  // Per tenant, on the server: the facility ID, flag and sequence every DIN
  // minted here is made from.

  getDinSettings: (tenantId: string): Promise<DinSettings> =>
    request<DinSettings>(`/products/din-settings`, tenantId),

  setDinSettings: (tenantId: string, settings: DinSettingsInput): Promise<DinSettings> =>
    request<DinSettings>(`/products/din-settings`, tenantId, {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),

  // ─── Created products (the "Create Product" action on a patient form) ──────

  listCreatedProducts: (
    tenantId: string,
    /**
     * `procedureId`: everything one run produced, across its forms.
     * `associatedSubmissionId`: what one form made, split or pooled, with the
     * whole split tree (z8nrz7fr1b).
     */
    params: {
      submissionId?: string
      procedureId?: string
      associatedSubmissionId?: string
      /** Every procedure of one patient. */
      patientMrn?: string
    } = {},
  ): Promise<CreatedProduct[]> => {
    const qs = new URLSearchParams()
    if (params.submissionId) qs.set('submissionId', params.submissionId)
    if (params.procedureId) qs.set('procedureId', params.procedureId)
    if (params.associatedSubmissionId) qs.set('associatedSubmissionId', params.associatedSubmissionId)
    if (params.patientMrn) qs.set('patientMrn', params.patientMrn)
    const query = qs.toString()
    return request<CreatedProduct[]>(`/products${query ? `?${query}` : ''}`, tenantId)
  },

  /**
   * ONE created product.
   *
   * Added for the product detail page an audit row links to — reading a single
   * product previously meant fetching the whole tenant's list and filtering.
   */
  getProduct: (tenantId: string, productId: string): Promise<CreatedProduct> =>
    request<CreatedProduct>(`/products/${productId}`, tenantId),

  /** What the product page shows beyond the product row (z8nrz7g57f). */
  getProductRecord: (tenantId: string, productId: string): Promise<ProductRecord> =>
    request<ProductRecord>(`/products/${encodeURIComponent(productId)}/record`, tenantId),

  createProduct: (tenantId: string, payload: CreateProductPayload): Promise<CreatedProduct> => {
    return request<CreatedProduct>(`/products`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  /**
   * Change a product's code: the server supersedes it with a new product under
   * `catalogEntryId`, keeping the DIN and the division suffix. Returns the new one.
   */
  changeProductCode: (
    tenantId: string,
    productId: string,
    payload: { catalogEntryId: string; reason: string },
  ): Promise<CreatedProduct> =>
    request<CreatedProduct>(`/products/${productId}/change-code`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateProductVolume: (
    tenantId: string,
    productId: string,
    payload: { volumeMl: number; reason: string; reasonDetail?: string },
  ): Promise<CreatedProduct> => {
    return request<CreatedProduct>(`/products/${productId}/volume`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  },

  /** One product's whole trail — what happened to this bag, newest first. */
  listProductAudit: (tenantId: string, productId: string): Promise<AuditTrailRow[]> => {
    return request<AuditTrailRow[]>(`/products/${productId}/audit`, tenantId)
  },

  poolProducts: (tenantId: string, payload: PoolProductsPayload): Promise<CreatedProduct> => {
    return request<CreatedProduct>(`/products/pool`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  /**
   * What a split would do to the product's lab results — read only, nothing is
   * created. The dialog's transparency panel renders this verbatim; the
   * formulas are the server's so the preview cannot disagree with what the
   * split then stores.
   */
  /**
   * What pooling the selection would produce — read only, nothing is created.
   * The mirror of `previewSplit`, and for the same reason: the formulas are
   * the server's, so the preview cannot disagree with what the pool stores.
   */
  previewPool: (tenantId: string, payload: PreviewPoolPayload): Promise<PoolPreview> => {
    return request<PoolPreview>(`/products/pool/preview`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  previewSplit: (
    tenantId: string,
    parentId: string,
    payload: PreviewSplitPayload,
  ): Promise<SplitPreview> => {
    return request<SplitPreview>(`/products/${parentId}/split/preview`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  splitProduct: (
    tenantId: string,
    parentId: string,
    payload: SplitProductPayload,
  ): Promise<CreatedProduct[]> => {
    return request<CreatedProduct[]>(`/products/${parentId}/split`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
}
